import type { ButtonProps, SxProps } from '@mui/material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import type { ReactNode, MouseEvent } from 'react';
import { useState } from 'react';
import { Button, Loader } from '../ui';

interface UseConfirmationDialogParams {
  slotProps?: {
    dialog?: {
      sx?: SxProps;
    };
  };
  title: string;
  description: ReactNode | ((context?: unknown) => ReactNode);
  acceptButtonText: string;
  acceptButtonColor: ButtonProps['color'];
  onAccept: (context?: unknown) => Promise<void>;
  onReject?: () => void;
}

export const useConfirmationDialog = ({
  slotProps,
  title,
  description,
  acceptButtonText,
  acceptButtonColor,
  onAccept,
  onReject,
}: UseConfirmationDialogParams): {
  openDialog: (context?: unknown) => void;
  dialogElememnt: ReactNode;
} => {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<unknown | undefined>(undefined);
  const [processing, setProcessing] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setProcessing(false);
  };
  const handleAccept = (event: MouseEvent) => {
    event.stopPropagation();

    setProcessing(true);
    onAccept(context)
      .then(() => {
        handleClose();
      })
      .catch(() => {
        setProcessing(false);
      });
  };
  const handleReject = () => {
    onReject?.();
    handleClose();
  };

  return {
    openDialog: (context?: unknown) => {
      setOpen(true);
      setContext(context);
    },
    dialogElememnt: (
      <Dialog
        PaperProps={{ sx: { borderRadius: 2, ...slotProps?.dialog?.sx } }}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle sx={{ p: 2, fontSize: '1.5rem', lineHeight: '1.75rem' }}>
          {title}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, fontSize: '0.875rem' }}>
          {typeof description === 'function'
            ? description(context)
            : description}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'flex-start' }}>
          <Button
            sx={{ px: 2, py: 1.5, borderRadius: 2, height: 40 }}
            color={acceptButtonColor}
            onClick={handleAccept}
          >
            {acceptButtonText}
          </Button>
          <Button
            sx={{ px: 2, py: 1.5, borderRadius: 2, height: 40 }}
            color="secondary"
            variant="outlined"
            onClick={handleReject}
          >
            Cancel
          </Button>
        </DialogActions>
        <Loader active={processing} />
      </Dialog>
    ),
  };
};
